// Formatadores pt-BR dos padrões de dado do design system (DataFormats). Iguais
// aos do bundle, com dois ajustes para o que a API devolve:
// - valor monetário chega como texto ("5200.00"), então formatBRL converte antes;
// - data chega como "YYYY-MM-DD" ou ISO com fuso ("2026-10-07T00:00:00.000Z").
//   Sem hora, vale só a parte da data do texto: new Date() no fuso de Brasília
//   mostraria 06/10. Com hora (created_at etc.), o instante no fuso local.

const MONEY = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

function toNumber(v) {
  if (v == null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : null;
}

/** 8500 | "8500.00" → "R$ 8.500,00"; nulo, vazio ou inválido → "—". */
export function formatBRL(v) {
  const n = toNumber(v);
  if (n === null) return "—";
  // Intl usa espaço inseparável (U+00A0) entre "R$" e o número; mantido assim.
  return MONEY.format(n);
}

/** Valor da API ("5200.00") no texto do InputMoney da lib ("R$ 5.200,00"); vazio ou inválido → "". */
export function moneyInputText(v) {
  const n = toNumber(v);
  return n === null ? "" : MONEY.format(n);
}

/** minutos → "12h30", "8h", "0h45"; negativo com sinal. Nunca decimal. */
export function formatHours(minutes) {
  const n = toNumber(minutes);
  if (n === null) return "—";
  const neg = n < 0;
  const total = Math.round(Math.abs(n));
  const hh = Math.floor(total / 60);
  const mm = total % 60;
  return `${neg && total ? "-" : ""}${hh.toLocaleString("pt-BR")}h${mm ? String(mm).padStart(2, "0") : ""}`;
}

/** "12h30" | "12:30" | "12,5" | "12" → minutos; texto que não é hora → null. */
export function parseHours(s) {
  const t = String(s || "").trim().toLowerCase().replace(/\s/g, "");
  let m = t.match(/^(\d+)[h:](\d{1,2})?m?$/);
  if (m) return Number(m[1]) * 60 + Number(m[2] || 0);
  m = t.match(/^(\d+)(?:[,.](\d+))?h?$/);
  if (m) return Math.round(parseFloat(`${m[1]}.${m[2] || "0"}`) * 60);
  return null;
}

const MESES = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
const MESES_CURTOS = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Competência { month: 1-12, year } → "Out/2026" (curto) ou "outubro de 2026" (`long`). */
export function formatCompetencia(c, long) {
  if (!c) return "—";
  return long ? `${MESES[c.month - 1]} de ${c.year}` : `${MESES_CURTOS[c.month - 1]}/${c.year}`;
}

function onlyDigits(s) {
  return String(s || "").replace(/\D/g, "");
}

/** "12345678000190" → "12.345.678/0001-90" (parcial enquanto digita). */
export function formatCNPJ(v) {
  const d = onlyDigits(v).padEnd(14, "_").slice(0, 14);
  return d.replace(/^(.{2})(.{3})(.{3})(.{4})(.{2})$/, "$1.$2.$3/$4-$5").replace(/_/g, "").replace(/[./-]+$/, "");
}

/** "12345678909" → "123.456.789-09". */
export function formatCPF(v) {
  const d = onlyDigits(v).slice(0, 11);
  return d.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
}

/** CPF oculto no padrão LGPD: "***.456.789-**". */
export function maskCPF(v) {
  const f = formatCPF(v);
  return f.length === 14 ? `***.${f.slice(4, 11)}-**` : f;
}

/** CPF ou CNPJ pela quantidade de dígitos. */
export function formatDocument(v) {
  const d = onlyDigits(v);
  return d.length > 11 ? formatCNPJ(d) : formatCPF(d);
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
// Coluna de data serializada como instante: meia-noite em UTC ("2026-10-07T00:00:00.000Z").
const MIDNIGHT_UTC = /^(\d{4})-(\d{2})-(\d{2})T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)$/;

/**
 * Date no fuso local. "YYYY-MM-DD" vale sempre pela data do texto. Com `dateOnly`,
 * meia-noite em UTC também vale pela data do texto (é uma data, não um instante);
 * qualquer outro horário é um instante e vai para o fuso local, para um registro
 * das 22h30 de 07/10 não aparecer como 08/10.
 */
export function toDate(v, dateOnly = false) {
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    const m = v.match(DATE_ONLY) || (dateOnly ? v.match(MIDNIGHT_UTC) : null);
    if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  }
  return new Date(v);
}

function valid(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

const pad = (n) => String(n).padStart(2, "0");

/** → "07/10/2026"; vazio ou inválido → "—". */
export function formatDate(v) {
  if (!v) return "—";
  const d = toDate(v, true);
  if (!valid(d)) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** → "07/10/2026 às 14:32" no fuso local; vazio ou inválido → "—". */
export function formatDateTime(v) {
  if (!v) return "—";
  const d = toDate(v);
  if (!valid(d)) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} às ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const HTML_ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };

/**
 * Texto digitado por alguém (nome, motivo, observação) pronto para o `$text`/`$replaces`
 * do `Text` e do `Notice` da lib, que montam o HTML sem escapar. Nulo → "".
 */
export function escapeHtml(v) {
  if (v == null) return "";
  return String(v).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c]);
}
